import type { FC } from "react";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { MenuTrigger } from "react-aria-components";
import { useBloc } from "@blac/react";
import { AnimatePresence } from "framer-motion";

import type { DropdownActionSheetProps } from "./DropdownActionSheet.types";
import { DropdownActionSheetBloc } from "./DropdownActionSheet.bloc";
import { getButtonIcon } from "./actionIconMap";
import {
  Backdrop,
  FloatingHighlight,
  IconWrapper,
  InvisibleTrigger,
  ItemTitle,
  ShortcutLabel,
  LoadingContainer,
  LoadingSpinner,
  MenuHeader,
  MenuMessage,
  MenuTitle,
  MenuWrapper,
  PopoverContent,
  StyledMenu,
  StyledMenuItem,
  StyledPopover
} from "./DropdownActionSheet.styles";
import { ToggleVisual } from "atom/toggle/ToggleVisual";

const ASSUMED_WIDTH = 220;

const DropdownActionSheet: FC<DropdownActionSheetProps> = (props) => {
  const [{ open, placement, title, message, buttons, iconPosition }, bloc] =
    useBloc(DropdownActionSheetBloc, { props });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    bloc.setElements(highlightRef.current, menuRef.current);
  }, [bloc]);

  useLayoutEffect(() => {
    if (!props.triggerPosition || !open) {
      return;
    }

    if (triggerRef.current) {
      triggerRef.current.style.left = `${props.triggerPosition.x}px`;
      triggerRef.current.style.top = `${props.triggerPosition.y}px`;
    }

    bloc.updatePosition(props.triggerPosition);
  }, [props.triggerPosition, open, bloc]);

  useLayoutEffect(() => {
    if (!open || !popoverRef.current || !props.triggerPosition) {
      return;
    }

    const triggerPos = props.triggerPosition;
    const popover = popoverRef.current;

    const rafId = requestAnimationFrame(() => {
      const popoverRect = popover.getBoundingClientRect();
      const originX = Math.min(
        Math.max(0, triggerPos.x - popoverRect.left),
        ASSUMED_WIDTH
      );
      const sheetIsBelowTrigger = popoverRect.top >= triggerPos.y;
      const originY = sheetIsBelowTrigger ? "0%" : "100%";

      popover.style.transformOrigin = `${originX}px ${originY}`;
    });

    return () => cancelAnimationFrame(rafId);
  }, [open, props.triggerPosition]);

  useEffect(() => {
    bloc.updateProps(props);
  }, [props.loading, props.isOpen, props.options]);

  const handleItemHover = useCallback(
    (e: React.MouseEvent<Element>) => {
      bloc.updateHighlight(e.currentTarget as HTMLElement);
    },
    [bloc]
  );

  const handleItemFocus = useCallback(
    (e: React.FocusEvent<Element>) => {
      bloc.updateHighlight(e.currentTarget as HTMLElement);
    },
    [bloc]
  );

  const handleItemTouchStart = useCallback(
    (e: React.TouchEvent<Element>) => {
      bloc.updateHighlight(e.currentTarget as HTMLElement);
      bloc.setHighlightPressed(true);
    },
    [bloc]
  );

  const handleItemPressStart = useCallback(() => {
    bloc.setHighlightPressed(true);
  }, [bloc]);

  const handleItemPressEnd = useCallback(() => {
    bloc.setHighlightPressed(false);
  }, [bloc]);

  const handleMenuAction = useCallback(
    (key: React.Key) => {
      const button = buttons.find((b) => b.id === key);
      if (!button) {
        return;
      }
      if (button.type === "toggle") {
        bloc.toggleButton(button.id);
      } else {
        bloc.selectOption(button.id);
      }
    },
    [buttons, bloc]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && bloc.consumeSkipDismiss()) {
        return;
      }
      if (!isOpen && bloc.backdropDismiss) {
        bloc.dismiss();
      }
    },
    [bloc]
  );

  const isLoading = props.loading;

  const backdropAnimation = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15 }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <Backdrop
            onClick={bloc.dismiss}
            style={{ cursor: bloc.backdropDismiss ? "pointer" : "default" }}
            {...backdropAnimation}
          />
        )}
      </AnimatePresence>

      <MenuTrigger isOpen={open} onOpenChange={handleOpenChange}>
        <InvisibleTrigger ref={triggerRef} />

        <StyledPopover
          ref={popoverRef}
          placement={placement}
          offset={4}
          shouldFlip
          data-theme={props.options.theme ?? "default"}
        >
          <PopoverContent>
            {isLoading ? (
              <LoadingContainer>
                <LoadingSpinner />
              </LoadingContainer>
            ) : (
              <>
                {(title ?? message) && (
                  <MenuHeader>
                    {title && <MenuTitle>{title}</MenuTitle>}
                    {message && <MenuMessage>{message}</MenuMessage>}
                  </MenuHeader>
                )}

                <MenuWrapper ref={menuRef} onMouseLeave={bloc.hideHighlight}>
                  <FloatingHighlight ref={highlightRef} />
                  <StyledMenu
                    aria-label={title ?? "Actions"}
                    onAction={handleMenuAction}
                  >
                    {buttons.map((button) => {
                      const icon = getButtonIcon(button);
                      const isToggle = button.type === "toggle";
                      return (
                        <StyledMenuItem
                          key={button.id}
                          id={button.id}
                          textValue={button.title}
                          data-style={button.style ?? "default"}
                          data-has-icon={icon ? "true" : "false"}
                          data-is-selected={
                            button.selected ? "true" : undefined
                          }
                          data-icon-position={iconPosition}
                          data-divider={button.divider ? "true" : undefined}
                          onMouseEnter={handleItemHover}
                          {
                            // ignoring ts error "prop does not exist on type" but it actually does - issue with react-aria types
                            ...{
                              onFocus: handleItemFocus,
                              onTouchStart: handleItemTouchStart,
                              onTouchEnd: handleItemPressEnd,
                              onTouchCancel: handleItemPressEnd,
                              onMouseDown: handleItemPressStart,
                              onMouseUp: handleItemPressEnd,
                              onMouseLeave: handleItemPressEnd
                            }
                          }
                        >
                          {icon && iconPosition === "left" && (
                            <IconWrapper>{icon}</IconWrapper>
                          )}
                          <ItemTitle>{button.title}</ItemTitle>
                          {button.shortcut && (
                            <ShortcutLabel>{button.shortcut}</ShortcutLabel>
                          )}
                          {isToggle && (
                            <ToggleVisual
                              isSelected={button.toggled ?? false}
                            />
                          )}
                          {icon && iconPosition === "right" && (
                            <IconWrapper>{icon}</IconWrapper>
                          )}
                        </StyledMenuItem>
                      );
                    })}
                  </StyledMenu>
                </MenuWrapper>
              </>
            )}
          </PopoverContent>
        </StyledPopover>
      </MenuTrigger>
    </>
  );
};

export default DropdownActionSheet;
